import { useEffect, useState } from 'react';
import { ExternalLink, FileText, ImageOff } from 'lucide-react';
import { videoEmbed } from '@nu/shared';
import { api } from '../../api/client.js';
import RichDoc from './RichDoc.jsx';
import PdfViewer from './PdfViewer.jsx';

// A message body as the reader sees it. Text goes in as React text (never
// HTML); videos play inline only from the allowed sites, others are a link;
// PDFs open inside the app.
export default function MessageBlocks({ blocks }) {
  return (
    <div className="msg-body">
      {(blocks || []).map((b, i) => <Block key={i} b={b} />)}
    </div>
  );
}

function Block({ b }) {
  if (b.type === 'rich') return <RichDoc doc={b.doc} />;
  if (b.type === 'pdf') return <PdfCard b={b} />;
  if (b.type === 'heading') return <h3 className="msg-heading">{b.text}</h3>;
  if (b.type === 'text') return <p className="msg-text">{b.text}</p>;
  if (b.type === 'bullets') return <ul className="msg-bullets">{b.items.map((t, i) => <li key={i}>{t}</li>)}</ul>;
  if (b.type === 'table') {
    const [head, ...rest] = b.header ? b.rows : [null, ...b.rows];
    return (
      <div className="table-scroll msg-table-wrap">
        <table className="table msg-table">
          {head && <thead><tr>{head.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>}
          <tbody>{rest.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  }
  if (b.type === 'image') {
    return (
      <figure className="msg-figure">
        <MessageImage id={b.imageId} alt={b.caption || 'Picture'} />
        {b.caption && <figcaption>{b.caption}</figcaption>}
      </figure>
    );
  }
  if (b.type === 'video') {
    const src = videoEmbed(b.url);
    return (
      <figure className="msg-figure">
        {src
          ? <div className="msg-video"><iframe src={src} title={b.caption || 'Video'} allow="fullscreen; picture-in-picture; encrypted-media" allowFullScreen loading="lazy" referrerPolicy="strict-origin-when-cross-origin" /></div>
          : null}
        <figcaption>
          {b.caption && <span>{b.caption} </span>}
          <a href={b.url} target="_blank" rel="noopener noreferrer" className="msg-link"><ExternalLink size={14} aria-hidden="true" /> {src ? 'Open the video' : 'Watch the video'}</a>
        </figcaption>
      </figure>
    );
  }
  return null;
}

// Pictures need the sign-in, so they're fetched and shown from memory.
export function MessageImage({ id, alt }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    let made = null;
    setFailed(false);
    api(`/messages/images/${id}`, { as: 'blob' })
      .then((blob) => { if (!live) return; made = URL.createObjectURL(blob); setUrl(made); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [id]);
  if (failed) return <div className="msg-img-missing"><ImageOff size={22} aria-hidden="true" /> <span className="small muted">The picture shows when you have signal.</span></div>;
  if (!url) return <div className="msg-img-loading" aria-label="Loading picture" />;
  return <img className="msg-img" src={url} alt={alt} />;
}

function PdfCard({ b }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="pdf-card" onClick={() => setOpen(true)}>
        <span className="pdf-card-icon"><FileText size={22} aria-hidden="true" /></span>
        <span className="pdf-card-text">
          <strong>{b.name}</strong>
          {b.caption ? <span className="small muted">{b.caption}</span> : <span className="small muted">PDF · tap to open</span>}
        </span>
        <span className="pdf-card-open">Open</span>
      </button>
      {open && <PdfViewer fileId={b.fileId} name={b.name} onClose={() => setOpen(false)} />}
    </>
  );
}
